import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Sheet,
  Typography,
  Stack,
  Switch,
  Alert,
  Select,
  Option,
} from '@mui/joy';
import { AutomationsAPI } from '../../lib/apiAutomations';
import type { Automation } from '../../types/automations';
import { Play as PlayArrow, FloppyDisk as Save } from '@phosphor-icons/react';
import { FeatureGate } from '../../components/common/FeatureGate';
import WorkflowCanvas from '../../components/automations/WorkflowCanvas';
import AutomationRunHistory from '../../components/automations/AutomationRunHistory';

const emptyAutomation: Partial<Automation> = {
  name: '',
  description: '',
  is_active: true,
  triggers: [],
  actions: [],
  modifiers: [],
};

const AutomationEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [automation, setAutomation] = useState<Partial<Automation>>(emptyAutomation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testRunning, setTestRunning] = useState(false);
  const [sampleContext, setSampleContext] = useState('{}');
  const [previewTrigger, setPreviewTrigger] = useState('');
  const [dirty, setDirty] = useState(false);
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);

  // Check if we're on the /new route by looking at the pathname
  const isNew = !id || window.location.pathname.includes('/new');

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      AutomationsAPI.retrieve(Number(id))
        .then(value => { setAutomation(value); setDirty(false); })
        .catch((err) => {
          setError('Failed to load automation');
          console.error(err);
        })
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const handleSave = async () => {
    if (!automation.name) {
      setError('Name is required');
      return;
    }

    if (!automation.triggers || automation.triggers.length === 0) {
      setError('At least one trigger is required');
      return;
    }

    if (!automation.actions || automation.actions.length === 0) {
      setError('At least one action is required');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (isNew) {
        const created = await AutomationsAPI.create(automation);
        if (created && created.id) {
          setAutomation(created);
          setDirty(false);
          navigate(`/advanced/automations/${created.id}`, { replace: true });
        } else {
          throw new Error('Created automation does not have an ID');
        }
      } else if (id) {
        setAutomation(await AutomationsAPI.update(Number(id), automation));
        setDirty(false);
        setError(null);
      }
    } catch (err: unknown) {
      const error = err as { message?: string; responseData?: unknown; response?: { data?: { detail?: string; message?: string } } };
      const errorMessage =
        (error.responseData ? JSON.stringify(error.responseData) : undefined) ||
        error.message ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to save automation';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTestRun = async () => {
    if (!id || isNew || dirty) {
      setError('Save your changes before previewing the workflow.');
      return;
    }

    setTestRunning(true);
    setTestResult(null);

    try {
      const context = JSON.parse(sampleContext);
      const result = await AutomationsAPI.testRun(Number(id), {
        trigger_type: previewTrigger || automation.triggers?.[0]?.trigger_type,
        context,
      });
      setTestResult(result);
    } catch (err: unknown) {
      const error = err as { message?: string; response?: { data?: { detail?: string } } };
      setError('Test run failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestRunning(false);
    }
  };

  if (loading && !isNew) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography level="h3">
          {isNew ? 'Create New Workflow' : automation.name || 'Edit Workflow'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/advanced/automations')}
          >
            Cancel
          </Button>
          {!isNew && (
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<PlayArrow />}
              onClick={handleTestRun}
              loading={testRunning}
            >
              Preview workflow
            </Button>
          )}
          <Button
            startDecorator={<Save />}
            onClick={handleSave}
            loading={loading}
          >
            Save
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isNew && <Sheet variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography level="title-md">Preview a sample event</Typography>
        <Typography level="body-sm">Save first, then preview with sample event fields. Preview does not send messages or change records.</Typography>
        <Select value={previewTrigger || automation.triggers?.[0]?.trigger_type || ''} onChange={(_, value) => setPreviewTrigger(value || '')} aria-label="Preview trigger" sx={{ my: 1 }}>
          {automation.triggers?.map((trigger, index) => <Option key={index} value={trigger.trigger_type}>{trigger.trigger_type.replaceAll('_', ' ')}</Option>)}
        </Select>
        <Textarea aria-label="Sample event JSON" value={sampleContext} onChange={event => setSampleContext(event.target.value)} minRows={2} placeholder='{"stage": "qualified", "hours_stalled": 48}' />
      </Sheet>}
      {testResult && <Alert color="neutral" sx={{ mb: 2, display: 'block' }}>
        <Typography>{String(testResult.message)}</Typography>
        <Typography>Trigger {testResult.trigger_matched ? 'matches' : 'does not match'} the sample event.</Typography>
        {(testResult.steps as Array<{ id: number; type: string; name: string; status: string; branch?: string }> || []).map(step =>
          <Typography key={`${step.type}-${step.id}`} level="body-sm">{step.name.replaceAll('_', ' ')}: {step.status.replaceAll('_', ' ')}{step.branch ? ` (${step.branch === 'true' ? 'Yes' : 'No'} branch)` : ''}</Typography>)}
      </Alert>}

      <Stack spacing={3}>
        {/* Basic Information */}
        <Sheet variant="outlined" sx={{ p: 3 }}>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Workflow Details
          </Typography>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Name</FormLabel>
              <Input
                value={automation.name || ''}
                onChange={(e) =>
                  (setDirty(true), setAutomation({ ...automation, name: e.target.value }))
                }
                placeholder="e.g., Send email when opportunity moves to qualified"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={automation.description || ''}
                onChange={(e) =>
                  (setDirty(true), setAutomation({ ...automation, description: e.target.value }))
                }
                placeholder="Optional description of what this workflow does"
                minRows={2}
              />
            </FormControl>

            <FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={automation.is_active || false}
                  onChange={(e) =>
                    (setDirty(true), setAutomation({ ...automation, is_active: e.target.checked }))
                  }
                  color={automation.is_active ? 'success' : 'neutral'}
                />
                <FormLabel sx={{ mb: 0 }}>Active</FormLabel>
              </Box>
              <Typography level="body-sm" sx={{ mt: 0.5 }}>
                When active, this workflow will run automatically when triggered
              </Typography>
            </FormControl>
          </Stack>
        </Sheet>

        {!isNew && id && <AutomationRunHistory automationId={Number(id)} />}
        {/* Workflow Canvas */}
        <Sheet variant="outlined" sx={{ p: 3 }}>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Workflow Builder
          </Typography>
          <WorkflowCanvas
            automation={automation as Automation}
            onUpdate={(updatedAutomation) => { setDirty(true); setAutomation(updatedAutomation); }}
          />
        </Sheet>
      </Stack>
    </Box>
  );
};

const AutomationEditorPageWithGate: React.FC = () => {
  return (
    <FeatureGate
      requiredTiers={['premium', 'enterprise']}
      featureName="Workflow Automations"
      featureDescription="Create powerful automated workflows to streamline your business processes and save time on repetitive tasks."
      benefits={[
        'Custom workflow automation builder',
        'Event-driven triggers from any module',
        'Multi-step action sequences',
        'Email and SMS notification automation',
        'Conditional logic and business rules',
        'Integration with all system modules',
        'Automation performance analytics',
        'Error handling and retry logic'
      ]}
    >
      <AutomationEditorPage />
    </FeatureGate>
  );
};

export default AutomationEditorPageWithGate;
